/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import './StudentExams.css';
import StudentExamTaking from './StudentExamTaking';
import { apiFetch } from './api';

function StudentExams({ studentId, user, onExamStatusChange, onExamStateChange }) {
  const [exams, setExams] = useState([]);
  const [archivedExams, setArchivedExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedExam, setSelectedExam] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [currentPage, setCurrentPage] = useState(null); // null, 'error'
  const [entranceConfirmation, setEntranceConfirmation] = useState('');
  const [pendingExamData, setPendingExamData] = useState(null);
  const [showEntranceConfirmationModal, setShowEntranceConfirmationModal] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [loadingArchived, setLoadingArchived] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date()); // Sınav başlama zamanı kontrolü için
  const isConfirmingRef = useRef(false);
  const hasNotifiedParentRef = useRef(false);
  const onExamStatusChangeRef = useRef(onExamStatusChange);
  const onExamStateChangeRef = useRef(onExamStateChange);
  
  // Callback'leri ref'te sakla
  useEffect(() => {
    onExamStatusChangeRef.current = onExamStatusChange;
    onExamStateChangeRef.current = onExamStateChange;
  }, [onExamStatusChange, onExamStateChange]);

  useEffect(() => {
    if (studentId) {
      fetchExams();
    }
  }, [studentId]);

  // Otomatik yenileme - 30 saniyede bir sessiz güncelleme
  useEffect(() => {
    if (!studentId) return;
    
    const interval = setInterval(() => {
      // Sessiz güncelleme - loading state'i değiştirme
      fetchExams(showArchived).catch(err => console.error('Otomatik yenileme hatası:', err));
    }, 30000); // 30 saniye

    return () => clearInterval(interval);
  }, [studentId, showArchived]);

  // Sınav başlama zamanı kontrolü için timer - sadece aktif sınavlar için
  useEffect(() => {
    if (showArchived || exams.length === 0) return;

    // Her 10 saniyede bir kontrol et (sistem yükü bindirmemek için, çoklu kullanıcı için optimize)
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 10000);

    return () => clearInterval(interval);
  }, [showArchived, exams]);

  // selectedExam ve attemptId set edildiğinde sayfayı otomatik kapat
  useEffect(() => {
    if (selectedExam && attemptId && currentPage === 'entrance-confirmation') {
      setCurrentPage(null);
      setPendingExamData(null);
    }
  }, [selectedExam, attemptId, currentPage]);

  // selectedExam ve attemptId değiştiğinde parent'a bildir
  useEffect(() => {
    const hasExam = !!selectedExam && !!attemptId;
    if (onExamStateChangeRef.current) {
      onExamStateChangeRef.current(hasExam);
    }
    if (hasExam && !hasNotifiedParentRef.current && onExamStatusChangeRef.current) {
      hasNotifiedParentRef.current = true;
      onExamStatusChangeRef.current(true);
    } else if (!hasExam && hasNotifiedParentRef.current && onExamStatusChangeRef.current) {
      hasNotifiedParentRef.current = false;
      onExamStatusChangeRef.current(false);
    }
  }, [selectedExam, attemptId]);

  const fetchExams = async (archived = false, silent = false) => {
    try {
      if (!silent) {
        if (archived) {
          setLoadingArchived(true);
        } else {
          setLoading(true);
        }
      }
      const response = await apiFetch(`/student/exams?archived=${archived}`);
      const data = await response.json();

      if (data.success) {
        if (archived) {
          setArchivedExams(data.exams || []);
        } else {
          setExams(data.exams || []);
        }
      } else {
        if (!silent) {
          setError(data.message || 'Sınavlar yüklenemedi');
        }
      }
    } catch (err) {
      if (!silent) {
        setError('Bağlantı hatası');
      }
      console.error('Error:', err);
    } finally {
      if (!silent) {
        if (archived) {
          setLoadingArchived(false);
        } else {
          setLoading(false);
        }
      }
    }
  };

  const handleShowArchived = () => {
    if (!showArchived) {
      setShowArchived(true);
      if (archivedExams.length === 0) {
        fetchExams(true);
      }
    } else {
      setShowArchived(false);
    }
  };

  // Backend'den gelen UTC formatındaki tarihi local timezone'a çevir (helper function)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Backend'den gelen tarih timezone-naive (direkt veritabanından)
    // ISO string formatında ama timezone bilgisi yok (örn: "2026-01-19T12:49:00")
    // Direkt parse et, timezone dönüşümü yapma
    // new Date() timezone-naive string'i local timezone olarak yorumlar
    // Ama biz direkt veritabanındaki saati göstermek istiyoruz
    // Bu yüzden string'i parse edip direkt göster
    const [datePart, timePart] = dateString.split('T');
    if (!datePart || !timePart) return '-';
    
    const [year, month, day] = datePart.split('-').map(Number);
    const [timeStr] = timePart.split('.');
    const [hour, minute, second = 0] = timeStr.split(':').map(Number);
    
    // Direkt veritabanındaki saati göster (timezone dönüşümü yok)
    return `${day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}.${year} ${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:${second.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'Draft': { label: 'Taslak', class: 'status-draft' },
      'Published': { label: 'Yayınlandı', class: 'status-published' },
      'Paused': { label: 'Duraklatıldı', class: 'status-paused' },
      'Completed': { label: 'Tamamlandı', class: 'status-completed' },
      'Archived': { label: 'Arşivlendi', class: 'status-archived' }
    };
    const statusInfo = statusMap[status] || { label: status, class: 'status-default' };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  const getAttemptStatusText = (attemptStatus) => {
    const statusMap = {
      'Submitted': 'Teslim Edildi',
      'InProgress': 'Devam Ediyor',
      'Timeout': 'Süre Doldu'
    };
    return statusMap[attemptStatus] || attemptStatus;
  };

  const handleStartExam = async (exam) => {
    // Sadece onay mesajı göster, API çağrısı yapma
    // attempt_count artırımı onay verildiğinde yapılacak (handleConfirmEntrance'da)
    const welcomeMessage = exam.attempt_status === 'InProgress' 
      ? 'Sınava devam etmek için hazır mısınız?'
      : (exam.entrance_confirmation || 'Sınava başlamak için hazır mısınız?');
    
    setEntranceConfirmation(welcomeMessage);
    setPendingExamData({ exam, attemptId: exam.attempt_id }); // attempt_id varsa kullan, yoksa null
    setShowEntranceConfirmationModal(true);
  };
  
  const handleConfirmEntrance = async () => {
    if (pendingExamData && !isConfirmingRef.current) {
      isConfirmingRef.current = true;
      const { exam, attemptId: existingAttemptId } = pendingExamData;
      
      // Önce modal'ı kapat - böylece modal render edilmez
      setShowEntranceConfirmationModal(false);
      setPendingExamData(null);
      
      // Ref'i sıfırla ki useEffect içinde onExamStatusChange çağrılabilsin
      hasNotifiedParentRef.current = false;
      
      try {
        // Onay verildiğinde /start endpoint'ine istek at
        // Bu attempt_count'u artıracak ve attempt_id döndürecek
        const response = await apiFetch(`/student/exams/${exam.exam_id}/start`, {
          method: 'POST',
          body: JSON.stringify({})
        });
        
        const data = await response.json();
        
        if (data.success) {
          // attempt_id'yi al (yeni oluşturuldu veya mevcut attempt'in güncellenmiş hali)
          const finalAttemptId = data.attempt_id || existingAttemptId;
          
          // State'leri set et - bu component'i re-render edecek ve StudentExamTaking render edilecek
          // useEffect içinde onExamStatusChange çağrılacak
          setSelectedExam(exam);
          setAttemptId(finalAttemptId);
        } else {
          // Hata durumunda kullanıcıya bildir - error sayfası ile
          setErrorMessage(data.message || 'Sınav başlatılamadı');
          setCurrentPage('error');
          // Ref'i temizle
          setTimeout(() => {
            isConfirmingRef.current = false;
          }, 100);
        }
      } catch (err) {
        console.error('Error:', err);
        setErrorMessage('Sınav başlatılırken hata oluştu');
        setCurrentPage('error');
        // Ref'i temizle
        setTimeout(() => {
          isConfirmingRef.current = false;
        }, 100);
      }
      
      // Ref'i temizle - bir sonraki onay için hazır
      setTimeout(() => {
        isConfirmingRef.current = false;
      }, 100);
    }
  };

  const handleExamComplete = () => {
    setSelectedExam(null);
    setAttemptId(null);
    hasNotifiedParentRef.current = false; // Ref'i sıfırla, bir sonraki sınav için hazır
    if (onExamStatusChange) {
      onExamStatusChange(false);
    }
    fetchExams(); // Sınav listesini yenile
  };

  // Sınav durumu değiştiğinde parent'a bildir (fallback) - KALDIRILDI: handleStartExam zaten onExamStatusChange çağırıyor
  // useEffect hook'u state güncellemelerini yanlış yorumlayıp onExamStatusChange(false) çağırıyordu

  // Sınav alma ekranındaysak, o ekranı göster
  // ÖNEMLİ: Bu kontrol component'in EN ÜSTÜNDE olmalı, sınav listesi render edilmeden önce
  // Sayfa render kontrolü - modallar yerine sayfalar göster
  if (currentPage === 'error') {
    return (
      <div className="dashboard-section">
        <div className="page-header">
          <button className="back-button" onClick={() => setCurrentPage(null)}>
            ← Geri Dön
          </button>
          <h2>Bilgi</h2>
        </div>
        <div className="page-content">
          <div style={{ 
            padding: '1rem', 
            textAlign: 'center',
            marginBottom: '1rem',
            fontSize: '0.95rem',
            lineHeight: '1.6'
          }}>
            {errorMessage}
          </div>
          <div className="form-actions" style={{ justifyContent: 'center' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setCurrentPage(null)}
            >
              Tamam
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Eğer sınav seçildiyse, direkt sınav sayfasını göster
  // Sayfa açık olsa bile, sınav sayfası öncelikli
  if (selectedExam && attemptId) {
    return (
      <StudentExamTaking
        exam={selectedExam}
        studentId={studentId}
        attemptId={attemptId}
        user={user}
        onComplete={handleExamComplete}
      />
    );
  }

  const displayExams = showArchived ? archivedExams : exams;
  const isLoading = showArchived ? loadingArchived : loading;

  return (
    <div className="dashboard-section" style={{ minHeight: '400px' }}>
      <div className="exams-header">
        <h2 style={{ margin: 0 }}>Sınavlarım</h2>
        <button
          onClick={handleShowArchived}
          className="archive-toggle-button"
          style={{
            width: '180px',
            height: '40px',
            padding: '0',
            background: showArchived ? '#6c757d' : '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '0.9rem',
            transition: 'all 0.3s ease',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
          onMouseEnter={(e) => {
            if (!showArchived) {
              e.target.style.background = '#45a049';
            }
          }}
          onMouseLeave={(e) => {
            if (!showArchived) {
              e.target.style.background = '#4CAF50';
            }
          }}
        >
          {showArchived ? 'Aktif Sınavlar' : 'Arşivdeki Sınavlar'}
        </button>
      </div>
      {isLoading ? (
        <div className="loading-state" style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p>Yükleniyor...</p>
        </div>
      ) : displayExams.length === 0 ? (
        <div className="empty-state" style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
            {showArchived ? 'Arşivde sınav bulunmamaktadır.' : 'Henüz sınavınız bulunmamaktadır.'}
          </p>
        </div>
      ) : (
        <div className="exams-list">
          {displayExams.map((exam) => (
            <div key={exam.exam_id} className="exam-card">
              <div className="exam-header">
                <h3>{exam.title}</h3>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  {getStatusBadge(exam.status)}
                  {exam.session_count > 1 && exam.session_number && (
                    <span style={{
                      padding: '0.4rem 1rem',
                      borderRadius: '20px',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      whiteSpace: 'nowrap',
                      background: '#1a5f2e',
                      color: 'white'
                    }}>
                      {exam.session_number}. Oturum
                    </span>
                  )}
                  {showArchived && exam.attempt_status === 'Submitted' && (
                    <span className="status-badge" style={{
                      padding: '0.4rem 1rem',
                      borderRadius: '20px',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      whiteSpace: 'nowrap',
                      background: '#6c757d',
                      color: 'white'
                    }}>
                      Teslim Edildi
                    </span>
                  )}
                </div>
              </div>
              <div className="exam-details">
                {showArchived ? (
                  <>
                    <div className="exam-info-card exam-info-full">
                      <span className="info-label">Ders:</span>
                      <span className="info-value">{exam.course_name}</span>
                    </div>
                    <div className="exam-info-card">
                      <span className="info-label">Başlangıç:</span>
                      <span className="info-value">{formatDate(exam.start_at)}</span>
                    </div>
                    <div className="exam-info-card">
                      <span className="info-label">Bitiş:</span>
                      <span className="info-value">{formatDate(exam.end_at)}</span>
                    </div>
                    {(exam.weight || exam.exam_weight) && (
                      <div className="exam-info-card">
                        <span className="info-label">Ağırlık:</span>
                        <span className="info-value">
                          %{exam.weight || exam.exam_weight}
                        </span>
                      </div>
                    )}
                    {exam.duration_minutes && (
                      <div className="exam-info-card">
                        <span className="info-label">Süre:</span>
                        <span className="info-value">
                          {(() => {
                            if (exam.start_at && exam.end_at) {
                              const examWindowMinutes = Math.floor((new Date(exam.end_at) - new Date(exam.start_at)) / 60000);
                              const effectiveMinutes = Math.min(exam.duration_minutes, examWindowMinutes);
                              return `${effectiveMinutes} dakika`;
                            }
                            return `${exam.duration_minutes} dakika`;
                          })()}
                        </span>
                      </div>
                    )}
                    {exam.total_question_count !== undefined && exam.total_question_count !== null && exam.total_question_count > 0 && (
                      <div className="exam-info-card">
                        <span className="info-label">Soru Sayısı:</span>
                        <span className="info-value">
                          {`${exam.effective_question_count || exam.question_count || exam.total_question_count} soru`}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="exam-info-card exam-info-full">
                      <span className="info-label">Ders:</span>
                      <span className="info-value">{exam.course_name}</span>
                    </div>
                    <div className="exam-info-card">
                      <span className="info-label">Başlangıç:</span>
                      <span className="info-value">{formatDate(exam.start_at)}</span>
                    </div>
                    <div className="exam-info-card">
                      <span className="info-label">Bitiş:</span>
                      <span className="info-value">{formatDate(exam.end_at)}</span>
                    </div>
                    {(exam.weight || exam.exam_weight) && (
                      <div className="exam-info-card">
                        <span className="info-label">Ağırlık:</span>
                        <span className="info-value">
                          %{exam.weight || exam.exam_weight}
                        </span>
                      </div>
                    )}
                    {exam.duration_minutes && (
                      <div className="exam-info-card">
                        <span className="info-label">Süre:</span>
                        <span className="info-value">
                          {(() => {
                            if (exam.start_at && exam.end_at) {
                              const examWindowMinutes = Math.floor((new Date(exam.end_at) - new Date(exam.start_at)) / 60000);
                              const effectiveMinutes = Math.min(exam.duration_minutes, examWindowMinutes);
                              return `${effectiveMinutes} dakika`;
                            }
                            return `${exam.duration_minutes} dakika`;
                          })()}
                        </span>
                      </div>
                    )}
                    {exam.total_question_count !== undefined && exam.total_question_count !== null && exam.total_question_count > 0 && (
                      <div className="exam-info-card">
                        <span className="info-label">Soru Sayısı:</span>
                        <span className="info-value">
                          {`${exam.effective_question_count || exam.question_count || exam.total_question_count} soru`}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Buton - zaman kontrolü ile - önce render et ki absolute position çalışsın */}
              {!showArchived && exam.status === 'Published' && (() => {
                const now = currentTime;
                const startDate = exam.start_at ? new Date(exam.start_at) : null;
                const endDate = exam.end_at ? new Date(exam.end_at) : null;
                
                if ((endDate && endDate < now) || (startDate && startDate > now)) {
                  return null;
                }
                
                if (exam.attempt_status === 'Submitted') {
                  return null;
                }
                
                if (exam.attempt_status !== 'Submitted' && exam.exam_status_display !== 'SÜRESİ GEÇTİ') {
                  const maxAttempts = exam.max_attempts || 1;
                  const usedAttempts = exam.used_attempts || 0;
                  const hasAttemptsLeft = usedAttempts < maxAttempts;
                  const isInProgress = exam.attempt_status === 'InProgress';
                  
                  if (!isInProgress && !hasAttemptsLeft) {
                    return null;
                  }
                  
                  return (
                    <button
                      className="btn btn-primary exam-start-button"
                      onClick={() => handleStartExam(exam)}
                      style={{
                        padding: '0.6rem 1.15rem',
                        background: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '1rem',
                        transition: 'all 0.2s ease',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                        boxSizing: 'border-box'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#45a049';
                        e.target.style.boxShadow = '0 3px 6px rgba(0, 0, 0, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#4CAF50';
                        e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.15)';
                      }}
                    >
                      {exam.attempt_status === 'InProgress' ? 'Devam Et' : 'Başla'}
                    </button>
                  );
                }
                
                return null;
              })()}
              
              {/* Zaman durumuna göre badge göster - sadece bitmiş veya henüz başlamamış durumlar için */}
              {!showArchived && exam.status === 'Published' && (() => {
                const now = currentTime; // Timer'dan gelen güncel zaman
                const startDate = exam.start_at ? new Date(exam.start_at) : null;
                const endDate = exam.end_at ? new Date(exam.end_at) : null;
                
                // Sınav bitmişse
                if (endDate && endDate < now) {
                  return (
                    <div className="exam-warning-message" style={{ 
                      marginTop: '0.75rem', 
                      padding: '0.75rem 1rem', 
                      background: '#f8d7da', 
                      color: '#721c24', 
                      borderRadius: '8px', 
                      fontSize: '0.9rem', 
                      fontWeight: '600', 
                      textAlign: 'center',
                      border: '2px solid #dc3545',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                    }}>
                      Sınav Tarihi Geçmiş
                    </div>
                  );
                }
                
                // Sınav henüz başlamadıysa
                if (startDate && startDate > now) {
                  return (
                    <div className="exam-warning-message" style={{ 
                      marginTop: '1rem', 
                      padding: '1.5rem 2rem', 
                      background: '#f8f9fa', 
                      color: '#495057', 
                      borderRadius: '10px', 
                      fontSize: '1.1rem', 
                      fontWeight: '700', 
                      textAlign: 'center',
                      border: '2px solid #dee2e6',
                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                      lineHeight: '1.6',
                      letterSpacing: '0.3px'
                    }}>
                      Sınav Henüz Başlamadı
                    </div>
                  );
                }
                
                return null;
              })()}
              
              {/* Sınav teslim edildi bilgilendirmesi - sınav henüz bitmediyse */}
              {!showArchived && exam.status === 'Published' && exam.attempt_status === 'Submitted' && (() => {
                const now = currentTime; // Timer'dan gelen güncel zaman
                const endDate = exam.end_at ? new Date(exam.end_at) : null;
                
                // Sınav henüz bitmediyse bilgilendirme göster
                if (endDate && endDate >= now) {
                  return (
                    <div className="exam-info-message" style={{ 
                      marginTop: '0.75rem', 
                      padding: '0.75rem 1rem', 
                      background: '#d1ecf1', 
                      color: '#0c5460', 
                      borderRadius: '8px', 
                      fontSize: '0.9rem', 
                      fontWeight: '600', 
                      textAlign: 'center',
                      border: '2px solid #17a2b8',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                    }}>
                      Sınav Teslim Edildi
                    </div>
                  );
                }
                
                return null;
              })()}
              
            </div>
          ))}
        </div>
      )}
      
      {/* Sınava Giriş Onay Modal */}
      {showEntranceConfirmationModal && (
        <div className="modal-overlay" onClick={() => {
          setShowEntranceConfirmationModal(false);
          setPendingExamData(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Sınav Kuralları ve Onay</h3>
              <button className="modal-close" onClick={() => {
                setShowEntranceConfirmationModal(false);
                setPendingExamData(null);
              }}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ 
                padding: '1.5rem', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px',
                marginBottom: '1.5rem',
                whiteSpace: 'pre-wrap',
                lineHeight: '1.6',
                fontSize: '0.95rem'
              }}>
                {entranceConfirmation}
              </div>
              <div className="form-actions" style={{ justifyContent: 'flex-end', gap: '0.75rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEntranceConfirmationModal(false);
                    setPendingExamData(null);
                  }}
                >
                  İptal
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleConfirmEntrance}
                  style={{
                    background: '#4CAF50',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  Onaylıyorum ve Sınava Başla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentExams;
